import React, { useEffect, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Progress, Alert, Spinner } from 'reactstrap';

interface ProvisioningProgressProps {
  sessionId: string;
  onComplete?: (accessToken: string, subdomain: string) => void;
  onError?: (error: string) => void;
}

interface ProvisioningUpdate {
  step: string;
  message: string;
  percentage: number;
  status: 'InProgress' | 'Completed' | 'Failed';
  timestamp: string;
  error?: string;
}

const TenantProvisioningProgress: React.FC<ProvisioningProgressProps> = ({
  sessionId,
  onComplete,
  onError,
}) => {
  const [progress, setProgress] = useState<ProvisioningUpdate>({
    step: 'Conectando',
    message: 'Estabelecendo conexão...',
    percentage: 0,
    status: 'InProgress',
    timestamp: new Date().toISOString(),
  });
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    // Get API URL from environment or use default
    const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7276';
    const hubUrl = `${apiUrl}/hubs/tenant-provisioning`;

    // Create SignalR connection
    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .build();

    // Handle progress updates
    connection.on('ProvisioningProgress', (update: ProvisioningUpdate) => {
      console.log('Provisioning progress:', update);
      setProgress(update);

      if (update.status === 'Failed' && onError) {
        onError(update.error || update.message);
      }
    });

    // Start connection and join session
    const startConnection = async () => {
      try {
        await connection.start();
        console.log('SignalR connected, joining session:', sessionId);

        // Join the provisioning session group
        await connection.invoke('JoinProvisioningSession', sessionId);

        setProgress({
          step: 'Conectado',
          message: 'Conexão estabelecida, iniciando provisionamento...',
          percentage: 2,
          status: 'InProgress',
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error('SignalR connection error:', error);
        setConnectionError('Erro ao conectar ao servidor. Tentando novamente...');

        // Retry connection after 2 seconds
        setTimeout(() => {
          if (connection.state === HubConnectionState.Disconnected) {
            startConnection();
          }
        }, 2000);
      }
    };

    startConnection();

    // Cleanup on unmount
    return () => {
      if (connection.state === HubConnectionState.Connected) {
        connection.invoke('LeaveProvisioningSession', sessionId).catch(console.error);
        connection.stop().catch(console.error);
      }
    };
  }, [sessionId, onError]);

  // Determine progress bar color based on status
  const getProgressColor = () => {
    if (progress.status === 'Completed') return 'success';
    if (progress.status === 'Failed') return 'danger';
    return 'primary';
  };

  return (
    <div className="tenant-provisioning-progress">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">
            {progress.status === 'InProgress' && (
              <Spinner size="sm" className="me-2" color="primary" />
            )}
            {progress.status === 'Completed' && (
              <i className="ri-checkbox-circle-fill me-2 text-success fs-16"></i>
            )}
            {progress.status === 'Failed' && (
              <i className="ri-error-warning-fill me-2 text-danger fs-16"></i>
            )}
            {progress.step}
          </h6>
          <span className="text-muted">{progress.percentage}%</span>
        </div>

        <Progress
          value={progress.percentage}
          color={getProgressColor()}
          style={{ height: '8px' }}
          animated={progress.status === 'InProgress'}
        />

        <p className="text-muted mt-2 mb-0">
          <small>{progress.message}</small>
        </p>
      </div>

      {connectionError && (
        <Alert color="warning" className="mb-0">
          <i className="ri-alert-line me-2"></i>
          {connectionError}
        </Alert>
      )}

      {progress.status === 'Failed' && progress.error && (
        <Alert color="danger" className="mb-0">
          <strong>Erro:</strong> {progress.error}
        </Alert>
      )}

      <style>{`
        .tenant-provisioning-progress {
          animation: fadeIn 0.3s ease-in;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TenantProvisioningProgress;
